import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "../../components/ui/sonner";

const EMPTY = {
  title: "", client_label: "DEMO / CONCEPT", problem: "", requirement: "", application_type: "",
  delivery_model: "STANDARD", estimated_hours: "", timeline: "", features: [], result: "",
  image_url: "", testimonial: "", is_active: true, is_demo: false,
};

export default function AdminCaseStudies() {
  const [cases, setCases] = useState([]);
  const [editing, setEditing] = useState(null);
  const [featuresText, setFeaturesText] = useState("");

  const load = () => api.get("/admin/case-studies").then((res) => setCases(res.data)).catch(() => toast.error("Could not load case studies"));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ ...EMPTY }); setFeaturesText(""); };
  const openEdit = (cs) => { setEditing({ ...cs }); setFeaturesText((cs.features || []).join("\n")); };

  const save = async () => {
    const payload = { ...editing, features: featuresText.split("\n").map((f) => f.trim()).filter(Boolean) };
    try {
      if (editing.case_id) {
        await api.put(`/admin/case-studies/${editing.case_id}`, payload);
      } else {
        await api.post("/admin/case-studies", payload);
      }
      setEditing(null);
      load();
      toast.success("Case study saved");
    } catch {
      toast.error("Save failed");
    }
  };

  const remove = async (caseId) => {
    await api.delete(`/admin/case-studies/${caseId}`).catch(() => toast.error("Delete failed"));
    load();
    toast.success("Case study removed");
  };

  const set = (k) => (e) => setEditing({ ...editing, [k]: e.target?.value ?? e });

  return (
    <div data-testid="admin-case-studies-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-white">Case Studies</h1>
          <p className="mt-2 text-sm text-slate-400">Demo entries are removable. Publish genuine case studies only with client consent.</p>
        </div>
        <Button onClick={openNew} data-testid="case-new-btn" className="bg-flame hover:bg-flame-hover text-white font-heading font-bold">
          <Plus className="w-4 h-4 mr-2" /> NEW
        </Button>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {cases.map((cs) => (
          <div key={cs.case_id} data-testid={`admin-case-item-${cs.case_id}`} className="rounded-xl border border-white/10 bg-navy-800 p-6">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-heading font-bold text-white">{cs.title}</h3>
              <div className="flex gap-1.5 shrink-0">
                {cs.is_demo && <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 font-mono text-[8px]">DEMO</Badge>}
                <Badge className={`font-mono text-[8px] ${cs.is_active ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-white/10 text-slate-400 border-white/10"}`}>
                  {cs.is_active ? "LIVE" : "HIDDEN"}
                </Badge>
              </div>
            </div>
            <p className="mt-2 text-xs text-slate-500">{cs.delivery_model} · {cs.estimated_hours}</p>
            <p className="mt-3 text-xs text-slate-400 line-clamp-2">{cs.problem}</p>
            <div className="mt-5 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => openEdit(cs)} data-testid={`case-edit-${cs.case_id}`} className="border-white/20 text-slate-200">
                <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
              </Button>
              <Button size="sm" variant="outline" onClick={() => remove(cs.case_id)} data-testid={`case-delete-${cs.case_id}`} className="border-red-500/40 text-red-400 hover:bg-red-500/10">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="bg-navy-800 border-white/10 max-w-2xl max-h-[88vh] overflow-y-auto" aria-describedby={undefined}>
          {editing && (
            <div data-testid="case-edit-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-white">{editing.case_id ? "Edit case study" : "New case study"}</DialogTitle>
              </DialogHeader>
              <div className="mt-4 space-y-4">
                <Input placeholder="Title *" value={editing.title} onChange={set("title")} data-testid="case-form-title" className="bg-navy-900/60 border-white/10" />
                <Input placeholder="Client label (e.g. DEMO / CONCEPT)" value={editing.client_label} onChange={set("client_label")} data-testid="case-form-client" className="bg-navy-900/60 border-white/10" />
                <Textarea rows={3} placeholder="Client problem" value={editing.problem} onChange={set("problem")} data-testid="case-form-problem" className="bg-navy-900/60 border-white/10" />
                <Textarea rows={3} placeholder="Business requirement / solution" value={editing.requirement} onChange={set("requirement")} data-testid="case-form-requirement" className="bg-navy-900/60 border-white/10" />
                <div className="grid grid-cols-2 gap-4">
                  <Input placeholder="Application type" value={editing.application_type} onChange={set("application_type")} data-testid="case-form-type" className="bg-navy-900/60 border-white/10" />
                  <Select value={editing.delivery_model} onValueChange={set("delivery_model")}>
                    <SelectTrigger data-testid="case-form-model" className="bg-navy-900/60 border-white/10"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-navy-800 border-white/10">
                      <SelectItem value="STANDARD">STANDARD</SelectItem>
                      <SelectItem value="RUSH HOUR">RUSH HOUR</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input placeholder="Estimated hours (e.g. 45 hours)" value={editing.estimated_hours} onChange={set("estimated_hours")} data-testid="case-form-hours" className="bg-navy-900/60 border-white/10" />
                  <Input placeholder="Timeline (e.g. 6 working days)" value={editing.timeline} onChange={set("timeline")} data-testid="case-form-timeline" className="bg-navy-900/60 border-white/10" />
                </div>
                <Textarea rows={4} placeholder="Features — one per line" value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} data-testid="case-form-features" className="bg-navy-900/60 border-white/10" />
                <Textarea rows={2} placeholder="Outcome / result" value={editing.result} onChange={set("result")} data-testid="case-form-result" className="bg-navy-900/60 border-white/10" />
                <Input placeholder="Screenshot image URL" value={editing.image_url} onChange={set("image_url")} data-testid="case-form-image" className="bg-navy-900/60 border-white/10" />
                <Textarea rows={2} placeholder="Testimonial (only genuine, with consent)" value={editing.testimonial} onChange={set("testimonial")} data-testid="case-form-testimonial" className="bg-navy-900/60 border-white/10" />
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-3 text-xs text-slate-300">
                    <Switch checked={editing.is_active} onCheckedChange={(v) => setEditing({ ...editing, is_active: v })} data-testid="case-form-active" /> Published
                  </label>
                  <label className="flex items-center gap-3 text-xs text-slate-300">
                    <Switch checked={editing.is_demo} onCheckedChange={(v) => setEditing({ ...editing, is_demo: v })} data-testid="case-form-demo" /> Demo / concept
                  </label>
                </div>
                <Button onClick={save} disabled={!editing.title} data-testid="case-form-save" className="w-full bg-flame hover:bg-flame-hover text-white font-heading font-bold">
                  SAVE CASE STUDY
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
